import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Alert, Row, Col } from 'react-bootstrap';
import SEO from '../components/common/SEO';
import SkeletonLoader, { EventHeaderSkeleton, EventContentSkeleton, CategoryCardSkeleton, SeatMapSkeleton } from '../components/common/SkeletonLoader';
import { apiService } from '../services/api';
import { 
  EventHeader, 
  Organizer, 
  EventContent, 
  PhaseTimer, 
  Categories,
  Addons
} from '../components/event/common';
import { EventChatIntegration } from '../components/chat';
import { TicketForm, MultiCategoryTicketForm } from '../components/event/v2';
import { V4CategoriesListing } from '../components/event/v4';
import ImageMap from '../components/event/v4/ImageMap';
import { WhitelistTemplate } from '../components/event/whitelist/index';
import { 
  V3TicketForm, 
  V3MultiCategoryTicketForm, 
  V3Categories, 
  V3Addons,
  V3ImageMap,
  V3Whitelist
} from '../components/event/v3';
import './EventDetailPage.scss';

interface EventDetailData {
  id: number;
  title: string;
  slug: string;
  event_type: string;
  starts: string;
  ends: string;
  created: string;
  modified: string;
  published: boolean;
  venue: {
    id: number;
    name: string;
    address: string;
    city: string;
    country: string;
    get_google_map_url?: string;
    landmark?: {
      google_map_url?: string;
    };
  };
  content: {
    description: string;
    policy: string;
    thumbnail: string;
    banner: string;
    header_video_url?: string;
    creatives_label: string;
    button_label: string;
    sold_out_text: string;
    thumbnail_url: string;
    banner_url: string;
  };
  setting: {
    status: string;
    event_page_template: string;
    live: boolean;
    whitelist_only: boolean;
    whitelist_message: string;
    on_page_registration: boolean;
    multi_category_registration: boolean;
    show_remaining_passes: boolean;
    show_remaining_in_percentage: boolean;
    hide_when_finished: boolean;
    registration_only: boolean;
    allow_standalone_addon_purchase: boolean;
    on_pass_policy: boolean;
    seated: boolean;
    allow_memories: boolean;
    memories_require_attendance: boolean;
    auto_approve_memories: boolean;
    highlight: boolean;
    status_display: string;
    sold_out: boolean;
    show_phase_timer: boolean;
    next_phase_ends?: string;
    phase_timer_label: string;
    collect_individual_information: boolean;
    registration_limit: number;
    currency: {
      symbol: string;
    };
  };
  categories: any[];
  organized_categories: {
    unclustered: any[];
    clustered: Record<string, any>;
  };
  addons: any[];
  grouped_by_cluster?: Record<string, any[]>;
  clusters?: Array<{
    id: number;
    name: string;
    description?: string;
    addons_count?: number;
  }>;
  event_fields: any[];
  images: any[];
  creatives: any[];
  sponsors: any[];
  organizer: any;
  trackers: any[];
  memories: any[];
  reviews: any[];
  partners: any[];
  attendee_count: number;
  revenue: number;
  price_range: string;
  duration_str: string;
  has_scanner_access: boolean;
  has_seated_registration: boolean;
  user_has_access: boolean;
  whitelist_fields: any[];
  image_map_data?: {
    image_url: string;
    title?: string;
    description?: string;
    processed_areas: Array<{
      type: 'rectangle' | 'circle' | 'polygon';
      coords: any;
      attributes: {
        id: string;
        title: string;
        href?: string;
        category: string;
      };
      processed_coords: string;
    }>;
    legend_items: Array<{
      tag: string;
      category_ids: string;
    }>;
    has_areas: boolean;
  };
}

const EventDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState<EventDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  
  // Progressive loading states
  const [loadingHeavy, setLoadingHeavy] = useState({
    images: false,
    addons: false,
    organizer: false,
    imageMap: false,
    memories: false,
    reviews: false,
  });

  // Stable callback for manual seating
  const handleManualSeating = useCallback(() => {
    console.log('Manual seating clicked');
    // Scroll to image map section
    const imageMapSection = document.querySelector('.image-map-section') as HTMLElement;
    if (imageMapSection) {
      imageMapSection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Add a subtle highlight effect
      imageMapSection.style.transition = 'all 0.3s ease';
      imageMapSection.style.boxShadow = '0 0 20px rgba(242, 63, 57, 0.3)';
      setTimeout(() => {
        imageMapSection.style.boxShadow = '';
      }, 2000);
    }
  }, []);

  // Stable callback for area click
  const handleAreaClick = useCallback((areaId: string, categoryId: string) => {
    console.log('EventDetailPage: Area clicked:', areaId, 'Category:', categoryId);
    console.log('EventDetailPage: Navigating to:', `/events/${eventData?.slug}/seated/${categoryId}`);
    // Navigate to seated registration for this category
    if (categoryId && eventData?.slug) {
      navigate(`/events/${eventData.slug}/seated/${categoryId}`);
    }
  }, [navigate, eventData?.slug]);

  // Stable callback for area hover
  const handleAreaHover = useCallback((areaId: string, categoryId: string) => {
    console.log('Area hovered:', areaId, 'Category:', categoryId);
    // Handle hover effects here
  }, []);

  // Stable callback for register click
  const handleRegisterClick = useCallback(() => {
    // Scroll to categories section
    const categoriesSection = document.querySelector('#event-pass-categories') as HTMLElement;
    if (categoriesSection) {
      categoriesSection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  }, []);

  // Build organized_categories from flat categories (for v3 template)
  const buildOrganizedCategories = useCallback((categories: any[]) => {
    if (!categories?.length) return { unclustered: [], clustered: {} as Record<string, any> };
    const unclustered = categories.filter((c: any) => !c.cluster || !c.cluster.id);
    const byCluster: Record<number, { cluster: any; categories: any[] }> = {};
    categories.forEach((c: any) => {
      if (c.cluster?.id != null) {
        const id = c.cluster.id;
        if (!byCluster[id]) {
          byCluster[id] = { cluster: c.cluster, categories: [] };
        }
        byCluster[id].categories.push(c);
      }
    });
    const clustered: Record<string, any> = {};
    Object.values(byCluster).forEach(({ cluster, categories: cats }) => {
      clustered[cluster.id] = { ...cluster, categories: cats };
    });
    return { unclustered, clustered };
  }, []);

  // Per-item loading: summary first, then parallel categories + addons, then lazy-load rest
  const fetchEventData = useCallback(async () => {
    if (!slug) {
      setError('Event not found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const summary = await apiService.events.getEventSummary(slug);
      const initialData: EventDetailData = {
        ...summary,
        created: '',
        modified: '',
        categories: [],
        organized_categories: { unclustered: [], clustered: {} },
        addons: [],
        event_fields: [],
        images: [],
        creatives: [],
        sponsors: [],
        organizer: null,
        trackers: [],
        memories: [],
        reviews: [],
        partners: [],
        attendee_count: 0,
        revenue: 0,
        price_range: '',
        duration_str: '',
        has_scanner_access: false,
        has_seated_registration: false,
        whitelist_fields: [],
      };
      setEventData(initialData);
      setLoading(false);

      // Parallel: categories, addons, content, and image map (when seated) so everything loads together
      const needsImageMap = !!summary.setting?.seated;
      setLoadingHeavy(prev => ({
        ...prev,
        addons: true,
        images: true,
        ...(needsImageMap ? { imageMap: true } : {}),
      }));
      const parallelPromises: Promise<unknown>[] = [
        apiService.events.getEventCategories(slug),
        apiService.events.getEventAddons(slug),
        apiService.events.getEventContent(slug),
      ];
      if (needsImageMap) {
        parallelPromises.push(apiService.events.getEventImageMap(slug));
      }
      const results = await Promise.allSettled(parallelPromises);
      const categoriesRes = results[0] as PromiseSettledResult<any[]>;
      const addonsRes = results[1] as PromiseSettledResult<{ addons: any[] }>;
      const contentRes = results[2] as PromiseSettledResult<{ content?: any; images?: any[] }>;
      const imageMapRes = needsImageMap ? (results[3] as PromiseSettledResult<EventDetailData['image_map_data']>) : null;

      if (categoriesRes.status === 'fulfilled' && Array.isArray(categoriesRes.value)) {
        const categories = categoriesRes.value;
        const organized_categories = buildOrganizedCategories(categories);
        setEventData(prev => prev ? { ...prev, categories, organized_categories } : prev);
      }
      if (addonsRes.status === 'fulfilled' && addonsRes.value?.addons) {
        setEventData(prev => prev ? { ...prev, addons: addonsRes.value.addons } : prev);
      }
      if (contentRes.status === 'fulfilled' && contentRes.value) {
        const data = contentRes.value;
        setEventData(prev =>
          prev
            ? {
                ...prev,
                content: prev.content ? { ...prev.content, ...data?.content } : (data?.content as any),
                images: data?.images ?? prev.images,
              }
            : prev
        );
      }
      if (imageMapRes?.status === 'fulfilled' && imageMapRes.value != null) {
        const imageMapData = imageMapRes.value as EventDetailData['image_map_data'];
        setEventData(prev => (prev ? { ...prev, image_map_data: imageMapData ?? undefined } : prev));
      }
      setLoadingHeavy(prev => ({
        ...prev,
        addons: false,
        images: false,
        ...(needsImageMap ? { imageMap: false } : {}),
      }));

      if (summary.setting?.whitelist_only && !summary.user_has_access) {
        apiService.events.getEventWhitelistFields(slug).then(fields => {
          setEventData(prev => (prev ? { ...prev, whitelist_fields: fields ?? [] } : prev));
        });
      }

      const loadOrganizer = async () => {
        setLoadingHeavy(prev => ({ ...prev, organizer: true }));
        try {
          const organizer = await apiService.events.getEventOrganizer(slug);
          setEventData(prev => (prev ? { ...prev, organizer: organizer ?? null } : prev));
        } catch {
          setEventData(prev => (prev ? { ...prev, organizer: null } : prev));
        } finally {
          setLoadingHeavy(prev => ({ ...prev, organizer: false }));
        }
      };
      setTimeout(loadOrganizer, 500);
    } catch (err: any) {
      console.error('Error fetching event details:', err);
      if (err.response?.status === 404) setError('Event not found');
      else setError('Failed to load event details');
      setLoading(false);
    }
  }, [slug, buildOrganizedCategories]);

  useEffect(() => {
    fetchEventData();
  }, [slug, fetchEventData]);

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await apiService.auth.getCurrentUser();
        setUser(userData.user);
      } catch (error) {
        // User not authenticated, that's fine
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  // Listen for country changes and refresh event data
  useEffect(() => {
    const handleCountryChange = () => {
      apiService.cache.clearByPattern('^event_');
      setTimeout(() => {
        fetchEventData();
      }, 200);
    };

    window.addEventListener('countryChanged', handleCountryChange as EventListener);
    return () => {
      window.removeEventListener('countryChanged', handleCountryChange as EventListener);
    };
  }, [slug, fetchEventData]);

  // Initialize lightbox functionality
  useEffect(() => {
    const initializeLightbox = () => {
      const lightboxModal = document.getElementById('lightboxModal');
      if (!lightboxModal) return;

      const handleModalShow = (event: Event) => {
        const button = (event as any).relatedTarget;
        if (!button) return;

        const imageSrc = button.getAttribute('data-bs-image');
        const imageCaption = button.getAttribute('data-bs-caption');

        const modalImage = lightboxModal.querySelector('#lightboxImage') as HTMLImageElement;
        const modalCaption = lightboxModal.querySelector('#lightboxCaption') as HTMLParagraphElement;
        const modalTitle = lightboxModal.querySelector('.modal-title') as HTMLHeadingElement;

        if (modalImage) modalImage.src = imageSrc || '';
        if (modalCaption) modalCaption.textContent = imageCaption || '';
        if (modalTitle) modalTitle.textContent = imageCaption || '';
      };

      lightboxModal.addEventListener('show.bs.modal', handleModalShow);

      return () => {
        lightboxModal.removeEventListener('show.bs.modal', handleModalShow);
      };
    };

    // Initialize lightbox after component mounts
    const cleanup = initializeLightbox();
    return cleanup;
  }, [eventData]); // Re-initialize when event data changes

  if (loading) {
    return (
      <div className="event-detail-page">
        <EventHeaderSkeleton />
        <Container className="container-xl mt-4">
          <Row className="event-content-row">
            <Col md={7}>
              <EventContentSkeleton />
            </Col>
            <Col md={5}>
              <div className='card p-4' style={{marginTop: "2rem"}}>
                <CategoryCardSkeleton />
                <CategoryCardSkeleton />
                <CategoryCardSkeleton />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!eventData) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <Alert.Heading>Event Not Found</Alert.Heading>
          The event you're looking for doesn't exist or has been removed.
        </Alert>
      </Container>
    );
  }

  // Check if event is whitelist only and user doesn't have access
  if (eventData.setting.whitelist_only && !eventData.user_has_access) {
    return <WhitelistTemplate event={eventData} user={user} />;
  }

  // Generate SEO data for event
  const eventTitle = `${eventData.title} | Quicket`;
  const eventDescription = (eventData.content as any)?.meta_description || 
    (eventData.content?.description ? 
      eventData.content.description.replace(/<[^>]*>/g, '').substring(0, 160) : 
      `Buy tickets for ${eventData.title}`);
  const eventImage = eventData.content?.banner_url || eventData.content?.thumbnail_url;
  const eventUrl = typeof window !== 'undefined' ? window.location.href : '';
  const canonicalUrl = (eventData.content as any)?.canonical_url || eventUrl;

  // Generate structured data for event
  const eventStructuredData = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": eventData.title,
    "description": eventDescription,
    "startDate": eventData.starts,
    "endDate": eventData.ends || undefined,
    "image": eventImage ? (eventImage.startsWith('http') ? eventImage : `${typeof window !== 'undefined' ? window.location.origin : ''}${eventImage}`) : undefined,
    "url": eventUrl,
    ...(eventData.venue && {
      "location": {
        "@type": "Place",
        "name": eventData.venue.name,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": eventData.venue.address || undefined,
          "addressLocality": eventData.venue.city || undefined,
          "addressCountry": eventData.venue.country || undefined,
        },
        ...(eventData.venue.get_google_map_url && {
          "geo": {
            "@type": "GeoCoordinates",
            "url": eventData.venue.get_google_map_url
          }
        })
      }
    }),
    ...(eventData.categories && eventData.categories.length > 0 && {
      "offers": eventData.categories
        .filter((cat: any) => cat.price !== null && cat.price !== undefined)
        .map((cat: any) => ({
          "@type": "Offer",
          "price": cat.price,
          "priceCurrency": eventData.setting?.currency?.symbol || "BDT",
          "availability": cat.available ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
          "url": eventUrl,
        }))
    })
  };

  // Render v2 template based on event settings - Building like a lego
  const renderV2Template = () => {
    return (
      <div className="event-detail-page v2-template">
        {/* Event Header Section */}
        <EventHeader 
          event={eventData} 
          onRegisterClick={handleRegisterClick}
        />

        <Container className="container-xl">
          <Row className="event-content-row">
            {/* Left Column - Event Content */}
            <Col md={7}>
              <EventContent event={eventData} loadingHeavy={loadingHeavy} />
            </Col>

            {/* Right Column - Categories and Registration */}
            <Col md={5}>
            <div className='card p-4' style={{marginTop: "2rem"}}>
              <div className="event-content-categories" id="event-pass-categories">
                <h3 className="section-title text-left mb-4 text-primary">
                  <i className="fas fa-ticket-alt me-2"></i>Categories
                </h3>

                {/* Phase Timer */}
                {eventData.setting.show_phase_timer && eventData.setting.next_phase_ends && (
                  <PhaseTimer event={eventData} />
                )}

                {/* Registration Forms - Based on settings flags */}
     
                {eventData.setting.live ? (
                  eventData.setting.on_page_registration ? (
                    eventData.setting.multi_category_registration ? (
                      <MultiCategoryTicketForm 
                        event={eventData}
                        categories={eventData.categories}
                      />
                    ) : (
                      eventData.categories && eventData.categories.length > 0 ? (
                        <TicketForm event={eventData} categories={eventData.categories} />
                      ) : (
                        <div className="alert alert-warning text-center">
                          <i className="fas fa-exclamation-circle me-2"></i>
                          No ticket categories are currently available for this event.
                        </div>
                      )
                    )
                  ) : (
                    <Categories event={eventData} user={user} />
                  )
                ) : (
                  <Categories event={eventData} user={user} />
                )}
              </div>
              </div>

              {/* Addons Section */}
              {eventData.addons && eventData.addons.length > 0 && (
                <Addons 
                  addons={eventData.addons} 
                  groupedByCluster={eventData.grouped_by_cluster}
                  clusters={eventData.clusters}
                />
              )}
            </Col>
          </Row>
        </Container>

        {/* Organizer Section - After EventContent container */}
        {eventData.organizer && (
          <Container className="container-xl">
            <Organizer organizer={eventData.organizer} event={eventData} />
          </Container>
        )}
      </div>
    );
  };

  // Render v3 template
  const renderV3Template = () => {
    // Check if event is seated and has image map data
    const isSeatedWithImageMap = eventData.setting.seated && eventData.image_map_data;
    
    // Transform image_map_data to match V3ImageMap expected format
    const transformedImageMapData = eventData.image_map_data ? {
      image_url: eventData.image_map_data.image_url,
      has_areas: eventData.image_map_data.has_areas,
      processed_areas: eventData.image_map_data.processed_areas.map((area: any) => ({
        id: area.attributes?.id || '',
        type: area.type,
        coords: area.processed_coords || area.coords,
        href: area.attributes?.href,
        title: area.attributes?.title,
        category: area.attributes?.category ? parseInt(area.attributes.category) : undefined,
        processed_coords: area.processed_coords
      })),
      legend_items: eventData.image_map_data.legend_items.map((item: any) => ({
        tag: item.tag,
        category_ids: item.category_ids ? (typeof item.category_ids === 'string' 
          ? item.category_ids.split(',').map((id: string) => parseInt(id.trim()))
          : Array.isArray(item.category_ids) ? item.category_ids.map((id: any) => parseInt(String(id)))
          : []) : []
      }))
    } : undefined;

    // Transform organized_categories to match V3Categories expected format
    const transformedOrganizedCategories = eventData.organized_categories ? {
      unclustered: eventData.organized_categories.unclustered || [],
      clustered: eventData.organized_categories.clustered 
        ? Object.entries(eventData.organized_categories.clustered).map(([name, categories]: [string, any]) => ({
            name,
            categories: Array.isArray(categories) ? categories : [],
            price_range: '', // Will be calculated by component if needed
          }))
        : []
    } : undefined;

    return (
      <div className="event-detail-page v3-template">
        {/* Event Header Section */}
        <EventHeader 
          event={eventData} 
          onRegisterClick={handleRegisterClick}
        />

        <Container className="container-xl">
          <Row className="event-content-row">
            {isSeatedWithImageMap ? (
              <>
                {/* Left Column - Image Map for Seated Events */}
                <Col md={7}>
                  <div className="image-map-section">
                    <V3ImageMap 
                      imageMapData={transformedImageMapData}
                    />
                  </div>
                </Col>

                {/* Right Column - Categories */}
                <Col md={5}>
                  <div className="event-content-categories" id="event-pass-categories">
                    {/* Phase Timer */}
                    {eventData.setting.show_phase_timer && eventData.setting.next_phase_ends && (
                      <PhaseTimer event={eventData} />
                    )}

                    {/* V3 Categories for seated events */}
                    <V3Categories 
                      event={eventData}
                      organizedCategories={transformedOrganizedCategories}
                    />
                  </div>
                </Col>
              </>
            ) : (
              <Col md={12}>
                <div className="event-content-categories" id="event-pass-categories">
                  {/* Phase Timer */}
                  {eventData.setting.show_phase_timer && eventData.setting.next_phase_ends && (
                    <PhaseTimer event={eventData} />
                  )}

                  {/* Registration Forms - Based on settings flags */}
                  {eventData.setting.live ? (
                    eventData.setting.on_page_registration ? (
                      eventData.setting.multi_category_registration ? (
                        <V3MultiCategoryTicketForm 
                          event={eventData}
                          categories={eventData.categories}
                          quota={eventData.setting?.registration_limit}
                        />
                      ) : (
                        eventData.categories && eventData.categories.length > 0 ? (
                          <V3TicketForm 
                            event={eventData} 
                            categories={eventData.categories} 
                          />
                        ) : (
                          <div className="alert alert-warning text-center">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            No ticket categories are currently available for this event.
                          </div>
                        )
                      )
                    ) : (
                      <V3Categories 
                        event={eventData}
                        organizedCategories={transformedOrganizedCategories}
                      />
                    )
                  ) : (
                    eventData.setting.status === 'scheduled' ? (
                      <h4 className="text-center">Coming Soon!</h4>
                    ) : (
                      <V3Categories 
                        event={eventData}
                        organizedCategories={transformedOrganizedCategories}
                      />
                    )
                  )}
                </div>

                {/* V3 Addons Section */}
                {loadingHeavy.addons ? (
                  <div className="d-flex justify-content-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading addons...</span>
                    </div>
                  </div>
                ) : eventData.addons && eventData.addons.length > 0 ? (
                  <V3Addons 
                    addonsData={{
                      has_addons: true,
                      processed_addons: eventData.addons
                    }}
                  />
                ) : null}
              </Col>
            )}
          </Row>
        </Container>

        {/* Event Content Section (About, Gallery, Creatives, Sponsors) */}
        <Container className="container-xl">
          <Row>
            <Col md={12}>
              <EventContent event={eventData} loadingHeavy={loadingHeavy} />
            </Col>
          </Row>
        </Container>

        {/* Organizer Section */}
        {eventData.organizer && (
          <Container className="container-xl">
            <Organizer organizer={eventData.organizer} event={eventData} />
          </Container>
        )}

        {/* Lightbox Modal for Gallery */}
        <div className="modal fade" id="lightboxModal" tabIndex={-1} aria-labelledby="lightboxModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title" id="lightboxModalLabel"></h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body text-center p-0">
                <img id="lightboxImage" src="" alt="" className="img-fluid" />
                <p className="mt-2" id="lightboxCaption"></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render v4 template for seated events
  const renderV4Template = () => {
    return (
      <div className="event-detail-page v4-template">
        <SEO
          title={eventTitle}
          description={eventDescription}
          image={eventImage}
          type="event"
          url={eventUrl}
          canonicalUrl={canonicalUrl}
          structuredData={eventStructuredData}
        />
        <EventHeader 
          event={eventData} 
          onRegisterClick={handleRegisterClick}
        />
        <Container className="container-xl">
          <Row className="event-content-row">
            {/* Left Column - Event Content */}
              <Col md={7}>
                {loadingHeavy.imageMap ? (
                  <div className="image-map-section">
                    <div className="image-map-container card border-0 shadow-sm overflow-hidden">
                      <div className="card-body p-0">
                        <div className="p-3 border-bottom">
                          <SkeletonLoader type="text" width={180} height={28} className="mb-0" />
                        </div>
                        <div className="p-3">
                          <SeatMapSkeleton />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : eventData.image_map_data ? (
                  <div className="image-map-section">
                    <ImageMap 
                      event={eventData}
                      onAreaClick={handleAreaClick}
                      onAreaHover={handleAreaHover}
                    />
                  </div>
                ) : null}
            </Col>

            {/* Right Column - Seated Event Categories */}
            <Col md={5}>
              <div className="event-content-categories p-3 glass" id="event-pass-categories">
                <h3 className="section-title mb-4 text-primary">
                  <i className="fas fa-ticket-alt me-2"></i>Seat Categories
                </h3>

                {/* Phase Timer */}
                {eventData.setting.show_phase_timer && eventData.setting.next_phase_ends && (
                  <PhaseTimer event={eventData} />
                )}

                {/* V4 Categories Listing */}
                <V4CategoriesListing 
                  event={eventData}
                  user={user}
                  onManualSeating={handleManualSeating}
                />
              </div>
            </Col>
            <Col md={12}>
            <EventContent event={eventData} loadingHeavy={loadingHeavy} />
            </Col>
          </Row>
        </Container>

        {/* Organizer Section */}
        {eventData.organizer && (
          <Container className="container-xl">
            <Organizer organizer={eventData.organizer} event={eventData} />
          </Container>
        )}

        {/* Lightbox Modal for Gallery */}
        <div className="modal fade" id="lightboxModal" tabIndex={-1} aria-labelledby="lightboxModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title" id="lightboxModalLabel"></h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body text-center p-0">
                <img id="lightboxImage" src={undefined} alt="" className="img-fluid" />
                <p className="mt-2" id="lightboxCaption"></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Check template type and render accordingly
  if (eventData.setting.event_page_template === 'v2') {
    return renderV2Template();
  }

  if (eventData.setting.event_page_template === 'v3') {
    return renderV3Template();
  }

  if (eventData.setting.event_page_template === 'v4' || eventData.setting.seated) {
    return renderV4Template();
  }

  // Default template (fallback) - Using new component structure
  return (
    <div className="event-detail-page">
      <SEO
        title={eventTitle}
        description={eventDescription}
        image={eventImage}
        type="event"
        url={eventUrl}
        canonicalUrl={canonicalUrl}
        structuredData={eventStructuredData}
      />
      <EventHeader 
        event={eventData} 
        onRegisterClick={handleRegisterClick}
      />
      <Container>
        <EventContent event={eventData} loadingHeavy={loadingHeavy} />
        
        {/* Organizer Section - After EventContent container */}
        {eventData.organizer && (
          <Organizer organizer={eventData.organizer} event={eventData} />
        )}
        
        {eventData.categories && eventData.categories.length > 0 && (
          <Categories event={eventData} user={user} />
        )}
        {eventData.setting.on_page_registration && (
          <TicketForm event={eventData} categories={eventData.categories} />
        )}
        
        {/* Chat Integration */}
        <div className="mt-4">
          <EventChatIntegration 
            eventId={eventData.id}
            eventTitle={eventData.title}
            eventSlug={eventData.slug}
          />
        </div>
      </Container>

      {/* Lightbox Modal for Gallery */}
      <div className="modal fade" id="lightboxModal" tabIndex={-1} aria-labelledby="lightboxModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header border-0">
              <h5 className="modal-title" id="lightboxModalLabel"></h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body text-center p-0">
              <img id="lightboxImage" src="" alt="" className="img-fluid" />
              <p className="mt-2" id="lightboxCaption"></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailPage;
